import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Label,
  FormFeedback,
  Button,
  Card,
  CardBody,
  CardTitle,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  ModalFooter,
  Container,
  Table,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import PurchaseService from "../../../services/OrderingServices/PurchaseService";
import { Link, useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Select from "react-select";
import ProductItemServices from "../../../services/OrderingServices/ProductItemService";
import ItemService from "../../../services/OrderingServices/ItemService";
import SupplierService from "../../../services/OrderingServices/SupplierService";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

// Validation Schema
const validationSchema = Yup.object({
  purchaseDate: Yup.date().required("Purchase date and time is required"),
  supplierId: Yup.number().required("Supplier is required"),
  items: Yup.array()
    .of(
      Yup.object({
        productId: Yup.number().required("Product is required"),
        productName: Yup.string().required("Product name is required"),
        quantity: Yup.number()
          .min(1, "Quantity must be at least 1")
          .required("Quantity is required"),
        unitPrice: Yup.number()
          .min(0, "Price cannot be negative")
          .required("Price is required"),
      }),
    )
    .min(1, "At least one item is required"),
});

const PurchaseForm = () => {
  const { id } = useParams();
  const [orgDetail, setOrgDetail] = useState(null);
  const [productData, setProductData] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const flatpickrRef = useRef(null);

  useEffect(() => {
    const fetchOrgDetail = async () => {
      if (id) {
        try {
          const response = await PurchaseService.detail(id);
          setOrgDetail(response);
        } catch (error) {
          console.error("Error fetching purchase details:", error);
          toast.error("Failed to load purchase details");
        }
      }
    };
    fetchOrgDetail();
  }, [id]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await ItemService.itemList();
        setProductData(response);
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error("Failed to load products");
      }
    };
    fetchProduct();
  }, []);

  useEffect(() => {
    const fetchSupplier = async () => {
      try {
        setLoading(true);
        const response = await SupplierService.getList(1, 100);
        setSupplierList(response);
      } catch (error) {
        console.error("Error fetching suppliers:", error);
        toast.error("Failed to load suppliers");
      } finally {
        setLoading(false);
      }
    };
    fetchSupplier();
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      purchaseDate: orgDetail?.purchaseDate || "",
      supplierId: orgDetail?.supplierId || "",
      items: orgDetail?.items || [
        {
          productId: "",
          productName: "",
          quantity: "",
          unitPrice: "",
        },
      ],
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          id: parseInt(id),
          purchaseDate: new Date(values.purchaseDate).toISOString(),
          supplierId: parseInt(values.supplierId),
          items: values.items.map((item) => ({
            productId: parseInt(item.productId),
            productName: item.productName,
            quantity: parseInt(item.quantity),
            unitPrice: parseFloat(item.unitPrice),
          })),
        };

        if (id) {
          await PurchaseService.update(id, payload);
          toast.success("Purchase updated successfully");
        } else {
          await PurchaseService.create(payload);
          toast.success("Purchase created successfully");
          resetForm();
        }
        navigate("/purchase");
      } catch (error) {
        console.error("Error submitting form:", error);
        toast.error(
          error?.response?.data?.message || "Failed to save purchase",
        );
      }
    },
  });

  const breadcrumbItems = [
    { title: "Back To List", onClick: () => navigate(-1) },
  ];

  const productOptions =
    productData?.items?.map((type) => ({
      value: type.id,
      label: type.name,
      price: type.price, // Assuming productData items have a price field
    })) || [];

  const supplierOptions =
    supplierList?.map((supplier) => ({
      value: supplier.id,
      label: supplier.name,
    })) || [];

  const handleAddItem = () => {
    formik.setFieldValue("items", [
      ...formik.values.items,
      {
        productId: "",
        productName: "",
        quantity: "",
        unitPrice: "",
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    const newItems = formik.values.items.filter((_, i) => i !== index);
    formik.setFieldValue("items", newItems);
  };

  const handleProductChange = (index, selectedOption) => {
    formik.setFieldValue(
      `items[${index}].productId`,
      selectedOption ? selectedOption.value : "",
    );
    formik.setFieldValue(
      `items[${index}].productName`,
      selectedOption ? selectedOption.label : "",
    );
    formik.setFieldValue(
      `items[${index}].unitPrice`,
      selectedOption ? selectedOption.price || "" : "",
    );
  };

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Purchase Form"
          pageTitle="Purchase"
          breadcrumbItems={breadcrumbItems}
        />
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col lg={6}>
              <div className="form-floating mb-3">
                <Flatpickr
                  id="purchaseDate"
                  name="purchaseDate"
                  className={`form-control ${formik.touched.purchaseDate && formik.errors.purchaseDate
                      ? "is-invalid"
                      : ""
                    }`}
                  placeholder="Select date and time"
                  options={{
                    enableTime: true,
                    dateFormat: "Y-m-d H:i",
                    time_24hr: true,
                    defaultDate: formik.values.purchaseDate,
                  }}
                  value={formik.values.purchaseDate}
                  onChange={([date]) =>
                    formik.setFieldValue("purchaseDate", date)
                  }
                  onBlur={() => formik.setFieldTouched("purchaseDate", true)}
                  ref={flatpickrRef}
                />
                <Label for="purchaseDate">Purchase Date & Time</Label>
                {formik.touched.purchaseDate && formik.errors.purchaseDate && (
                  <FormFeedback>{formik.errors.purchaseDate}</FormFeedback>
                )}
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <Select
                  id="supplierId"
                  name="supplierId"
                  options={supplierOptions}
                  value={
                    supplierOptions.find(
                      (option) => option.value === formik.values.supplierId,
                    ) || null
                  }
                  onChange={(option) =>
                    formik.setFieldValue(
                      "supplierId",
                      option ? option.value : "",
                    )
                  }
                  placeholder="Select Supplier"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (baseStyles) => ({
                      ...baseStyles,
                      padding: "0.3rem",
                    }),
                  }}
                />
                {formik.touched.supplierId && formik.errors.supplierId && (
                  <div className="text-danger mt-1">
                    {formik.errors.supplierId}
                  </div>
                )}
              </div>
            </Col>
          </Row>
          <Card className="pt-2">
            <CardBody>
              <Row>
                <Col xs={12}>
                  <div className="d-flex justify-content-between mt-2 mb-2">
                    <h5>Items</h5>
                    <Button
                      color="success"
                      size="sm"
                      type="button"
                      onClick={handleAddItem}
                    >
                      <i className="bx bx-plus"></i> Add Item
                    </Button>
                  </div>
                  <Table bordered>
                    <thead>
                      <tr>
                        <th>S.N</th>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Unit Price</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formik.values.items && formik.values.items.length > 0 ? (
                        formik.values.items.map((item, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>
                              <Select
                                id={`items[${index}].productId`}
                                name={`items[${index}].productId`}
                                options={productOptions}
                                value={
                                  productOptions.find(
                                    (option) => option.value === item.productId,
                                  ) || null
                                }
                                onChange={(option) =>
                                  handleProductChange(index, option)
                                }
                                placeholder="Select Product"
                                isClearable
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                  control: (baseStyles) => ({
                                    ...baseStyles,
                                  }),
                                }}
                              />
                              {formik.touched.items?.[index]?.productId &&
                                formik.errors.items?.[index]?.productId && (
                                  <div className="text-danger mt-1">
                                    {formik.errors.items[index].productId}
                                  </div>
                                )}
                            </td>
                            <td>
                              <Input
                                name={`items[${index}].quantity`}
                                type="number"
                                value={item.quantity}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="Quantity"
                                invalid={
                                  !!(
                                    formik.touched.items?.[index]?.quantity &&
                                    formik.errors.items?.[index]?.quantity
                                  )
                                }
                              />
                              <FormFeedback>
                                {formik.errors.items?.[index]?.quantity}
                              </FormFeedback>
                            </td>
                            <td>
                              <Input
                                name={`items[${index}].unitPrice`}
                                readOnly
                                type="number"
                                value={item.unitPrice}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="Unit Price"
                                invalid={
                                  !!(
                                    formik.touched.items?.[index]?.unitPrice &&
                                    formik.errors.items?.[index]?.unitPrice
                                  )
                                }
                              />
                              <FormFeedback>
                                {formik.errors.items?.[index]?.unitPrice}
                              </FormFeedback>
                            </td>
                            <td>
                              <Button
                                color="danger"
                                size="sm"
                                type="button"
                                onClick={() => handleRemoveItem(index)}
                              >
                                <i className="bx bx-minus"></i>
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center p-lg-4 p-2">
                            Please add an item to view them here.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </CardBody>
          </Card>
          <div className="d-flex justify-content-end mt-3">
            <Link to="/purchase" className="me-2">
              <Button color="danger">Cancel</Button>
            </Link>
            <Button color="success" type="submit" disabled={loading}>
              {id ? "Update" : "Create"}
            </Button>
          </div>
        </Form>
      </div>
    </Container>
  );
};

export default PurchaseForm;
